import React from "react";
import {
  Document,
  Page,
  Image,
  View,
  Link,
  Text,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

export const PDF_PAGE_W_MM = 210;
export const PDF_PAGE_H_MM = 297;
export const PDF_MARGIN_MM = 14;
export const DEFAULT_SECTION_GAP_MM = 5;
/** Keep short sections (e.g. platform headers) with the following block when both fit on one page. */
const KEEP_WITH_NEXT_MAX_MM = 18;
/** Below this scale, prefer slicing a tall section across pages over unreadable shrink. */
const MIN_SCALE_TO_FIT = 0.75;

export function pdfContentBoxMm(footerReserveMm = 0): {
  maxW: number;
  maxH: number;
} {
  return {
    maxW: PDF_PAGE_W_MM - 2 * PDF_MARGIN_MM,
    maxH: PDF_PAGE_H_MM - 2 * PDF_MARGIN_MM - Math.max(0, footerReserveMm),
  };
}

function canvasHeightMm(canvas: HTMLCanvasElement, displayWMm: number): number {
  if (canvas.width <= 0) return 0;
  return (canvas.height / canvas.width) * displayWMm;
}

type CanvasPdfImagePlacement = {
  dataUrl: string;
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
  links?: CanvasPdfHotLink[];
};

/** Clickable overlay on a captured section, in ratios of the image box. */
export type CanvasPdfHotLink = {
  href: string;
  xRatio: number;
  yRatio: number;
  wRatio: number;
  hRatio: number;
};

type CanvasPdfPage = {
  images: CanvasPdfImagePlacement[];
};

function ensurePage(pages: CanvasPdfPage[], index: number): void {
  while (pages.length <= index) {
    pages.push({ images: [] });
  }
}

function sliceCanvasRegion(
  canvas: HTMLCanvasElement,
  srcY: number,
  slicePx: number,
): string {
  const sub = document.createElement("canvas");
  sub.width = canvas.width;
  sub.height = slicePx;
  const ctx = sub.getContext("2d");
  if (ctx) {
    ctx.drawImage(
      canvas,
      0,
      srcY,
      canvas.width,
      slicePx,
      0,
      0,
      canvas.width,
      slicePx,
    );
  }
  return sub.toDataURL("image/png");
}

function addTallCanvasPlacements(
  canvas: HTMLCanvasElement,
  pages: CanvasPdfPage[],
  pageIndex: number,
  startYMm: number,
  xMm: number,
  displayWMm: number,
  maxBodyHMm: number,
  marginMm: number,
): { pageIndex: number; yMm: number } {
  const displayHMm = canvasHeightMm(canvas, displayWMm);
  const mmPerPixelY = displayHMm / canvas.height;
  let srcY = 0;
  let y = startYMm;
  let pi = pageIndex;
  const pageBottom = marginMm + maxBodyHMm;

  while (srcY < canvas.height - 0.5) {
    ensurePage(pages, pi);

    let spaceLeft = pageBottom - y;
    if (spaceLeft < mmPerPixelY * 2) {
      pi += 1;
      ensurePage(pages, pi);
      y = marginMm;
      spaceLeft = pageBottom - y;
    }

    const slicePx = Math.min(
      canvas.height - srcY,
      Math.max(1, Math.floor(spaceLeft / mmPerPixelY)),
    );
    const sliceHMm = slicePx * mmPerPixelY;

    pages[pi].images.push({
      dataUrl: sliceCanvasRegion(canvas, srcY, slicePx),
      xMm,
      yMm: y,
      widthMm: displayWMm,
      heightMm: sliceHMm,
    });

    srcY += slicePx;
    y += sliceHMm;
  }

  return { pageIndex: pi, yMm: y };
}

/**
 * Packs section canvases onto A4 pages.
 * - A section that fits on one page is never split; if it doesn't fit the
 *   remaining space, it jumps to the next page.
 * - Short sections (headers) try to stay with the following section.
 * - Sections taller than a page scale down to fit when reasonable; otherwise slice.
 */
function layoutCanvasesOnPages(
  sectionCanvases: HTMLCanvasElement[],
  sectionGapMm: number,
  footerReserveMm = 0,
  sectionLinks: Array<CanvasPdfHotLink[] | undefined> = [],
): CanvasPdfPage[] {
  const { maxW, maxH } = pdfContentBoxMm(footerReserveMm);
  const pages: CanvasPdfPage[] = [{ images: [] }];
  let pageIndex = 0;
  let y = PDF_MARGIN_MM;
  const pageBottom = PDF_MARGIN_MM + maxH;

  const ensureSpaceOrNewPage = (neededMm: number) => {
    if (y + neededMm > pageBottom + 0.02) {
      pageIndex += 1;
      ensurePage(pages, pageIndex);
      y = PDF_MARGIN_MM;
    }
  };

  const placeFittedSection = (
    canvas: HTMLCanvasElement,
    displayWMm: number,
    displayHMm: number,
    links?: CanvasPdfHotLink[],
  ) => {
    ensurePage(pages, pageIndex);
    const xMm = PDF_MARGIN_MM;
    pages[pageIndex].images.push({
      dataUrl: canvas.toDataURL("image/png", 1.0),
      xMm,
      yMm: y,
      widthMm: displayWMm,
      heightMm: displayHMm,
      links,
    });
    y += displayHMm;
  };

  for (let i = 0; i < sectionCanvases.length; i += 1) {
    const canvas = sectionCanvases[i];
    const links = sectionLinks[i];
    const naturalHMm = canvasHeightMm(canvas, maxW);

    if (naturalHMm <= maxH + 0.02) {
      // Keep short headers with the next section when both fit on one page.
      let neededMm = naturalHMm;
      if (
        naturalHMm <= KEEP_WITH_NEXT_MAX_MM &&
        i + 1 < sectionCanvases.length
      ) {
        const nextHMm = canvasHeightMm(sectionCanvases[i + 1], maxW);
        const combined = naturalHMm + sectionGapMm + nextHMm;
        if (combined <= maxH + 0.02) {
          neededMm = combined;
        }
      }

      ensureSpaceOrNewPage(neededMm);
      placeFittedSection(canvas, maxW, naturalHMm, links);
    } else {
      const scale = maxH / naturalHMm;
      if (scale >= MIN_SCALE_TO_FIT) {
        // Slightly oversized: shrink to one page instead of splitting.
        ensureSpaceOrNewPage(maxH);
        placeFittedSection(canvas, maxW * scale, maxH, links);
      } else {
        // Too tall to shrink readably — start on a fresh page, then slice.
        if (y > PDF_MARGIN_MM + 0.02) {
          pageIndex += 1;
          ensurePage(pages, pageIndex);
          y = PDF_MARGIN_MM;
        }
        const result = addTallCanvasPlacements(
          canvas,
          pages,
          pageIndex,
          y,
          PDF_MARGIN_MM,
          maxW,
          maxH,
          PDF_MARGIN_MM,
        );
        pageIndex = result.pageIndex;
        y = result.yMm;
      }
    }

    if (i < sectionCanvases.length - 1) {
      y += sectionGapMm;
      if (y > pageBottom - 0.02) {
        pageIndex += 1;
        ensurePage(pages, pageIndex);
        y = PDF_MARGIN_MM;
      }
    }
  }

  return pages;
}

/** A4 in PDF points — must exist as in-flow content; absolute-only pages collapse to height 0. */
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#FFFFFF",
  },
  /** In-flow spacer so Yoga keeps A4 height when images/footer are position:absolute. */
  pageFrame: {
    width: A4_WIDTH_PT,
    height: A4_HEIGHT_PT,
  },
  canvasImage: {
    position: "absolute",
  },
  hotLink: {
    position: "absolute",
  },
});

type ComposeCanvasPdfOptions = {
  sectionGapMm?: number;
  /** Space reserved at the bottom of each page (e.g. media-kit footer). */
  footerReserveMm?: number;
  footer?: React.ReactElement;
  /** Clickable overlays per captured section, aligned to that section's image box. */
  sectionLinks?: Array<CanvasPdfHotLink[] | undefined>;
};

function CanvasPdfDocument({
  pages,
  footer,
}: {
  pages: CanvasPdfPage[];
  footer?: React.ReactElement;
}): React.ReactElement {
  return (
    <Document>
      {pages.map((page, pageIndex) => (
        <Page key={`page-${pageIndex}`} size="A4" style={styles.page} wrap={false}>
          <View style={styles.pageFrame} />
          {page.images.map((img, imageIndex) => (
            <React.Fragment key={`img-${pageIndex}-${imageIndex}`}>
              <Image
                src={img.dataUrl}
                style={[
                  styles.canvasImage,
                  {
                    left: `${img.xMm}mm`,
                    top: `${img.yMm}mm`,
                    width: `${img.widthMm}mm`,
                    height: `${img.heightMm}mm`,
                  },
                ]}
              />
              {(img.links ?? []).map((link, linkIndex) => (
                <Link
                  key={`link-${pageIndex}-${imageIndex}-${linkIndex}`}
                  src={link.href}
                  style={[
                    styles.hotLink,
                    {
                      left: `${img.xMm + link.xRatio * img.widthMm}mm`,
                      top: `${img.yMm + link.yRatio * img.heightMm}mm`,
                      width: `${Math.max(2, link.wRatio * img.widthMm)}mm`,
                      height: `${Math.max(4, link.hRatio * img.heightMm)}mm`,
                    },
                  ]}
                >
                  <Text> </Text>
                </Link>
              ))}
            </React.Fragment>
          ))}
          {footer}
        </Page>
      ))}
    </Document>
  );
}

/** Assembles html2canvas captures into a multi-page A4 PDF blob. */
export async function composeCanvasesToPdfBlob(
  sectionCanvases: HTMLCanvasElement[],
  options: ComposeCanvasPdfOptions = {},
): Promise<Blob> {
  const pages = layoutCanvasesOnPages(
    sectionCanvases,
    options.sectionGapMm ?? DEFAULT_SECTION_GAP_MM,
    options.footerReserveMm ?? 0,
    options.sectionLinks ?? [],
  );
  const element = (
    <CanvasPdfDocument pages={pages} footer={options.footer} />
  );
  return pdf(element).toBlob();
}
